import { PaymentStatus } from '@/api/ariadne/__generated_types__/apollo';
import {
  TAX_STATUS_SELECTOR_OPTIONS,
  TOP_LEVEL_TOT_TEST_IDS,
  TOP_LEVEL_TOT_VIEW_STATUS_COLOR_PALETTE_MAP,
} from '@/top-level-tot/constants';
import { AGGridColoredCellRenderer } from 'deckard-ui/dist/components/AGGridTable/cell-renderers/colored-cell/ag-grid-colored-cell-renderer';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { InputPicker } from 'rsuite';
import { InputItemDataType } from 'rsuite/esm/InputPicker';

function isPaymentStatus(value: string): value is PaymentStatus {
  return Object.values<string>(PaymentStatus).includes(value);
}

function SelectableStatus({ label, value }: InputItemDataType) {
  if (typeof label !== 'string') return;
  if (typeof value !== 'string') return;
  if (!isPaymentStatus(value)) return;

  return (
    <AGGridColoredCellRenderer
      label={label}
      colorPalette={TOP_LEVEL_TOT_VIEW_STATUS_COLOR_PALETTE_MAP[value]}
      tailwindClasses="!m-0"
    />
  );
}

interface TaxStatusStateProps {
  taxStatus?: PaymentStatus;
  onChange: (value: PaymentStatus) => void;
}

export function TaxStatusFilter(props: TaxStatusStateProps): JSX.Element {
  const { taxStatus, onChange } = props;

  const [internalValue, setInternalValue] = useState<PaymentStatus | undefined>(
    taxStatus
  );

  const debouncedOnChange = useMemo(
    () => debounce((value: PaymentStatus) => onChange(value), 250),
    [onChange]
  );

  const onDropdownSelect = useCallback(
    (value: PaymentStatus) => {
      setInternalValue(value);
      debouncedOnChange(value);
    },
    [debouncedOnChange, setInternalValue]
  );

  /**
   * Returning the cancel method, so that when the component is unmounted
   * then all residual debounced calls will be cleaned up.
   */
  useEffect(() => debouncedOnChange.cancel, [debouncedOnChange]);

  return (
    <InputPicker
      data-testid={TOP_LEVEL_TOT_TEST_IDS.PAYMENT_PERIOD_TAX_STATUS_DROPDOWN}
      placeholder="All Tax Statuses"
      data={TAX_STATUS_SELECTOR_OPTIONS}
      value={internalValue}
      onChange={onDropdownSelect}
      renderMenuItem={(_, item) => SelectableStatus(item)}
    />
  );
}
